import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCase from "@hooks/case/useCase";
import useDataLayer from "@hooks/useDataLayer";
import {
  medicationRemoved,
  selectRemovedMedications,
} from "@store/removedMedications/removedMedicationsSlice";
import { GA4Events } from "@types-runway/analytics";
import { Case } from "@types-runway/case";
import { Condition } from "@types-runway/condition";
import { RemovedMedication } from "@types-runway/removedMedication";
import { RootEntity, RootState } from "@types-runway/state";

export const withoutCondition = (conditions: Condition[], productName: string): Condition[] =>
  conditions.filter((condition) => condition.product.name !== productName);

export const withConditionRestored = (
  conditions: Condition[],
  removed: RemovedMedication
): Condition[] => {
  const isAlreadyInOrder = conditions.some(
    (condition) => condition.product.name === removed.condition.product.name
  );
  if (isAlreadyInOrder) return conditions;

  const positionOf = (productName: string) => removed.productOrder.indexOf(productName);
  const restoredPosition = positionOf(removed.condition.product.name);
  const insertAt = conditions.findIndex(
    (condition) => positionOf(condition.product.name) > restoredPosition
  );
  if (insertAt === -1) return [...conditions, removed.condition];

  return [...conditions.slice(0, insertAt), removed.condition, ...conditions.slice(insertAt)];
};

const useRemoveMedication = () => {
  const dispatch = useDispatch();
  const newCase = useSelector<RootState, RootEntity<Case>>((state) => state.case.newCase);
  const removedMedications = useSelector<RootState, RemovedMedication[]>((state) =>
    selectRemovedMedications(state, state.case.newCase.data.id)
  );
  const pendingRemovalRef = useRef<RemovedMedication | null>(null);
  const { updateCase } = useCase();
  const { reportRemoveFromCart } = useDataLayer(GA4Events.REMOVE_FROM_CART);

  // The removed medication is remembered only once the save has succeeded.
  useEffect(() => {
    const pending = pendingRemovalRef.current;
    if (!pending || newCase.isPending) return;

    pendingRemovalRef.current = null;
    const isRemoved = !(newCase.data.conditions ?? []).some(
      (condition) => condition.product.name === pending.condition.product.name
    );
    if (isRemoved && newCase.data.id) {
      dispatch(medicationRemoved({ caseId: newCase.data.id, ...pending }));
    }
  }, [newCase.isPending, newCase.data.conditions]);

  const removeMedication = useCallback(
    (productName: string) => {
      const conditions = newCase.data.conditions ?? [];
      const removedIndex = conditions.findIndex(
        (condition) => condition.product.name === productName
      );
      const caseId = newCase.data.id;

      if (newCase.isPending || removedIndex === -1 || !caseId) return;

      const removed = conditions[removedIndex];
      // Nothing new can join the order, so the latest removal already knows the full order.
      const knownOrder = removedMedications[removedMedications.length - 1]?.productOrder;
      pendingRemovalRef.current = {
        condition: removed,
        productOrder: knownOrder ?? conditions.map((condition) => condition.product.name),
      };
      updateCase(caseId, Boolean(newCase.data.UserId), {
        conditions: withoutCondition(conditions, productName),
      });
      reportRemoveFromCart(removed);
    },
    [newCase, removedMedications, updateCase, reportRemoveFromCart]
  );

  return { removeMedication };
};

export default useRemoveMedication;
